import { Button, Modal, Spin, message } from "antd";
import React, { useEffect, useRef, useState } from "react";

import styles from "@/components/ERenewGenerateQR/ERenewGenerateQR.module.css";
import WarningMessage from "@/components/WarningMessage";
import { generateRenewPaynowQR, getRenewPaymentStatus } from "@/services/Pledge/PledgeApis";

interface RenewGenerateQRProps {
  open: boolean;
  close: () => void;
  qrGeneratePayload: any;
  onPaymentSuccess: (id: number) => void;
}
const ERenewGenerateQR: React.FC<RenewGenerateQRProps> = ({
  open,
  close,
  qrGeneratePayload,
  onPaymentSuccess,
}) => {
  const [paynowQR, setPaynowQR] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [timer, setTimer] = useState(60 * 15);

  useEffect(() => {
    if (open) {
      void fetchPaynowQR();
    }
    return () => {
      stopPolling();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !paynowQR) return;
    setTimer(60 * 15);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setPaynowQR(null);
          stopPolling();
          close();
          message.error("QR code expired. Please generate a new QR.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, paynowQR]);

  const startPolling = (id: number) => {
    stopPolling();

    intervalRef.current = setInterval(() => {
      void checkPaymentStatus(id);
    }, 2000);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const checkPaymentStatus = async (id: number) => {
    try {
      const response = await getRenewPaymentStatus({
        consolidated_id: id,
      });

      if (response?.success) {
        message.success("Payment Successful");
        onPaymentSuccess(id);
        stopPolling();
        close();
      }
    } catch (error) {
      console.error("Error checking PayNow payment status:", error);
    }
  };

  const fetchPaynowQR = async () => {
    setLoading(true);
    try {
      const response = await generateRenewPaynowQR(qrGeneratePayload);
      if (response?.qr) {
        setPaynowQR(`data:image/png;base64,${response.qr}`);
        startPolling(response?.consolidatedPayment);
      } else {
        setPaynowQR(null);
      }
    } catch (error) {
      console.error("Error generating PayNow QR code:", error);
    } finally {
      setLoading(false);
    }
  };
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };
  const downloadQRCode = async () => {
    if (!paynowQR) return;

    try {
      const response = await fetch(paynowQR);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "payment-qr.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading QR code:", error);
    }
  };

  return (
    <Modal
      open={open}
      title="QR Code"
      onCancel={close}
      closable={false}
      footer={null}
      maskClosable={false}
      centered
      width={"40vw"}
    >
      <Spin spinning={loading}>
        <div className={styles.modalWrapper}>
          <img src={paynowQR || undefined} alt="QR Code" className={styles.imageWidth} />
          <p className={styles.subText}>Scan this QR code to proceed with payment</p>
          <div className="my-2">
            <WarningMessage message="Payment is currently under processing. Please do not navigate to another page or close this window. You will be redirected automatically once the payment is completed successfully." />
          </div>
          <p className={styles.expiryText}> QR expires in {formatTime(timer)}</p>

          <Button className={styles.applyButton} onClick={downloadQRCode}>
            Download QR
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};
export default ERenewGenerateQR;
